import type { ReactNode } from 'react';
import { LinkingFlow, useSATConnection } from '@/features/sat-connection';
import { SyncStatusWidget } from '@/features/sat-connection/sync-status/components/SyncStatusWidget';
import { EVENT_TYPES } from '@/shared/constants/events';
import { useDeclarationsOverview } from '../hooks/useDeclarationsOverview';
import { useDeclarationsTrack } from '../hooks/useDeclarationsTrack';
import { needsLinking } from '../lib/fromEngine';
import { markReconnected, useCheckingReconnection } from '../lib/reconnection';

const DeclarationsSyncGate = ({ children }: { children: ReactNode }) => {
	const { overview, refetch } = useDeclarationsOverview();
	const { isLinked } = useSATConnection();
	const checking = useCheckingReconnection();
	const track = useDeclarationsTrack({ surface: 'sync_gate' });

	if (!needsLinking(overview)) return children;
	if (!isLinked) return <LinkingFlow origin='declarations' />;
	// sin_perfil con un vínculo viejo activo: fiscal-core todavía no tiene su
	// perfil, y esperar no lo crea. Volver a conectar sí (el carril de vínculos
	// lo da de alta), así que se ofrece igual que reconectar.
	if ((overview?.sync === 'reconectar' || overview?.sync === 'sin_perfil') && !checking) {
		const noProfile = overview?.sync === 'sin_perfil';
		return (
			<section className='mx-auto flex w-full max-w-xl flex-col gap-3 px-5 pt-6'>
				<p
					className={
						noProfile
							? 'text-h-text-heading rounded-2xl bg-blue-50 px-4 py-3 text-[15px] leading-relaxed font-medium'
							: 'bg-h-error-95 text-h-error-50 rounded-2xl px-4 py-3 text-[15px] leading-relaxed font-medium'
					}>
					{noProfile
						? 'Conecta de nuevo tu cuenta del SAT para traer tus facturas y ver tus declaraciones.'
						: 'No podemos entrar a tu cuenta del SAT con los datos que tenemos, así que no traemos tus facturas ni podemos presentar.'}
				</p>
				<LinkingFlow
					origin={noProfile ? 'declarations_no_profile' : 'declarations_reconnect'}
					relink
					syncUrl={false}
					onCompleted={() => {
						track(EVENT_TYPES.DECLARATIONS_SAT_RECONNECTED);
						markReconnected();
						void refetch();
					}}
				/>
			</section>
		);
	}
	return (
		<section className='mx-auto flex w-full max-w-xl flex-col items-center gap-4 px-5 pt-6'>
			{!checking && <SyncStatusWidget />}
			<p className='text-h-text-medium text-center text-[15px] leading-relaxed'>
				{checking
					? 'Ya recibimos tus datos del SAT. Estamos comprobando la conexión para traer tus facturas; puede tardar unos minutos.'
					: 'Estamos trayendo tus facturas del SAT para armar tus declaraciones. En unos minutos las ves aquí.'}
			</p>
		</section>
	);
};

export default DeclarationsSyncGate;
